<template>
  <div class="container">
    <div v-if="isPending">
      <div class="my-12 flex justify-center">
        <div class="flex flex-col px-4">
          <div
            class="mx-auto mb-1 text-xs font-bold uppercase tracking-wider text-white"
          >
            - Game code -
          </div>
          <div class="mx-auto text-xl uppercase tracking-wider text-white">
            {{ multiplayerGame.gameId }}
          </div>
          <div class="mt-2 flex flex-col items-center gap-2">
            <button
              class="btn btn--xsmall"
              @click="copyText(multiplayerGame.gameId, 'code')"
            >
              {{ isCodeCopied ? 'Copied' : 'Copy Code' }}
            </button>
            <button
              class="btn btn--xsmall"
              @click="
                copyText(
                  `https://tictactoe2.tjhyatt.com?code=${multiplayerGame.gameId}`,
                  'link'
                )
              "
            >
              {{ isLinkCopied ? 'Copied' : 'Copy Link' }}
            </button>
          </div>
        </div>

        <div class="flex flex-col px-4">
          <div
            class="mx-auto mb-1 text-xs font-bold uppercase tracking-wider text-white"
          >
            - Win Condition -
          </div>
          <div class="mx-auto text-xl uppercase tracking-wider text-white">
            {{ multiplayerGame.settings.winCondition }}
          </div>
        </div>
      </div>

      <div class="mt-8 flex flex-col text-center text-white">
        <div class="mb-2 text-sm">Waiting for players to join...</div>
        <div class="flex justify-center">
          <ol class="w-64 text-left" style="list-style: radial-gradient">
            <li
              v-for="player in multiplayerGame.settings.players"
              :key="player"
              class="mb-2"
            >
              <template
                v-if="
                  multiplayerGame.players[player - 1] &&
                  multiplayerGame.players[player - 1].username
                "
              >
                <span>
                  {{ multiplayerGame.players[player - 1].username }}
                </span>
                <span
                  v-if="
                    multiplayerGame.players[player - 1] &&
                    multiplayerGame.players[player - 1].userId === userId
                  "
                >
                  (You)
                </span>
                <span v-else-if="multiplayerGame.hostId === userId"
                  ><button
                    class="btn btn--xsmall"
                    @click="
                      leaveOnlineGame(
                        multiplayerGame.players[player - 1].userId
                      )
                    "
                  >
                    remove
                  </button></span
                >
              </template>
            </li>
          </ol>
        </div>

        <div
          v-if="
            multiplayerGame.players.length ===
              parseInt(multiplayerGame.settings.players) &&
            multiplayerGame.hostId === userId
          "
          class="mt-4"
        >
          <button class="btn" @click="startOnlineGame">Start game</button>
        </div>
      </div>
    </div>
    <div
      v-if="isActive"
      class="row flex"
      :class="{
        'pointer-events-none opacity-50': !isActive,
      }"
    >
      <!-- Win screen -->
      <transition name="fade">
        <div
          v-if="isActiveWinScreen && multiplayerGame.winner.length === 1"
          class="fixed bottom-0 left-0 right-0 top-0 z-50 bg-black bg-opacity-60"
        >
          <div
            class="fancy-text"
            :class="[
              { 'text-red-400': multiplayerGame.winner[0].playerNumber === 1 },
              { 'text-blue-400': multiplayerGame.winner[0].playerNumber === 2 },
              {
                'text-green-400': multiplayerGame.winner[0].playerNumber === 3,
              },
              {
                'text-yellow-400': multiplayerGame.winner[0].playerNumber === 4,
              },
              {
                'text-purple-400': multiplayerGame.winner[0].playerNumber === 5,
              },
            ]"
          >
            {{ multiplayerGame.winner[0].username }}<br />Wins!
          </div>
        </div>
      </transition>

      <!-- Draw -->
      <transition name="fade">
        <div
          v-if="isActiveWinScreen && multiplayerGame.winner.length > 1"
          class="fixed bottom-0 left-0 right-0 top-0 z-50 bg-black bg-opacity-60"
        >
          <div class="fancy-text text-gray-500">DRAW!</div>
        </div>
      </transition>

      <VBoardMultiplayer
        ref="vboardmultiplayer"
        :key="multiplayerGame.gameId"
        :user-id="userId"
        :game-id="multiplayerGame.gameId"
        :prop-data="multiplayerGame"
        @playTurn="playTurn"
      />
    </div>

    <div
      class="relative z-0 mb-8 mt-12 text-center text-xs text-white opacity-80"
    >
      Photo by
      <a
        class="underline"
        href="https://unsplash.com/@birminghammuseumstrust?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText"
        >Birmingham Museums Trust</a
      >
      on
      <a
        class="underline"
        href="https://unsplash.com/s/photos/abstract-art-painting?utm_source=unsplash&utm_medium=referral&utm_content=creditCopyText"
        >Unsplash</a
      >
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import VBoard from '@/components/VBoard'
import VBoardMultiplayer from '@/components/VBoardMultiplayer'
import { mapGetters } from 'vuex'
import { socket } from '../main'

export default {
  components: { VBoard, VBoardMultiplayer },

  data() {
    return {
      multiplayerGame: {},
      isActiveWinScreen: false,

      // other
      isLinkCopied: false,
      isCodeCopied: false,
      copiedTimeout: null,
    }
  },

  computed: {
    ...mapGetters(['getOnlineGame']),

    userId() {
      return this.getUserId()
    },

    isActive() {
      return (
        this.multiplayerGame.status === 'active' ||
        this.multiplayerGame.status === 'complete'
      )
    },

    isPending() {
      return this.multiplayerGame.status === 'pending'
    },
  },

  created() {
    this.fetchOnlineGame()
    this.socketConnection()

    socket.on('createdGame', (game) => {
      this.multiplayerGame = game
    })

    // socket.on('connectedToGame', (game) => {
    //   console.log('someone connectedToGame')
    // })

    socket.on('joinedGame', (game) => {
      this.multiplayerGame = JSON.parse(game)
    })

    socket.on('leftGame', (game) => {
      this.multiplayerGame = JSON.parse(game)
    })

    socket.on('startedGame', (game) => {
      this.multiplayerGame = JSON.parse(game)
    })

    socket.on('playedTurn', (game) => {
      this.multiplayerGame = JSON.parse(game)

      if (this.multiplayerGame.status === 'complete') {
        setTimeout(() => {
          this.isActiveWinScreen = true
        }, 2000)

        setTimeout(() => {
          this.isActiveWinScreen = false
        }, 5000)
      }
    })
  },

  methods: {
    async fetchOnlineGame() {
      const gameId = this.$route.params.gameId
      const { data } = await axios.get(
        `${process.env.API_HOST}v1/game/id/${gameId}`
      )

      this.multiplayerGame = data.game

      this.joinOnlineGame()

      if (this.multiplayerGame.status === 'complete') {
        setTimeout(() => {
          this.isActiveWinScreen = true
        }, 2000)

        setTimeout(() => {
          this.isActiveWinScreen = false
        }, 5000)
      }
    },

    socketConnection() {
      if (socket.disconnected) {
        socket.on('connect', () => {
          const engine = socket.io.engine

          engine.once('upgrade', () => {})
        })
      }
    },

    socketConnectToGame() {
      socket.emit('connectToGame', this.multiplayerGame)
    },

    socketLeaveRoom() {
      socket.emit('leaveRoom', this.multiplayerGame.gameId)
    },

    socketCreateGame() {
      const userId = this.getUserId()
      const multiplayerGame = {
        settings: {
          gridSize: this.gridSize,
          players: this.players,
          winCondition: this.winCondition,
        },
        boardState: [],
        hostId: userId,
        players: [
          {
            userId: userId,
            username: this.getUsername(),
            playerNumber: 1,
            score: 0,
          },
        ],
        isActive: false,
        activePlayer: 1,
        winner: [],
      }

      socket.emit('createGame', multiplayerGame)
      this.multiplayerGame = multiplayerGame
    },

    getUserId() {
      return window.localStorage.getItem('id')
    },

    getUsername() {
      return window.localStorage.getItem('username')
    },

    createOnlineGame() {
      this.socketConnection()
      this.socketCreateGame()
    },

    joinOnlineGame() {
      socket.emit('joinGame', {
        gameId: this.multiplayerGame.gameId.toUpperCase(),
        player: {
          userId: this.userId,
          username: this.getUsername(),
        },
      })
    },

    leaveOnlineGame(userId) {
      // user left the game or was booted
      socket.emit('leaveGame', {
        gameId: this.multiplayerGame.gameId.toUpperCase(),
        player: {
          userId: userId,
        },
      })
    },

    startOnlineGame() {
      socket.emit('startGame', {
        gameId: this.multiplayerGame.gameId,
        player: {
          userId: this.userId,
          username: this.getUsername(),
        },
      })
    },

    playTurn(data) {
      socket.emit('playTurn', data)
    },

    copyText(string, type) {
      navigator.clipboard.writeText(string)

      if (this.copiedTimeout) {
        clearTimeout(this.copiedTimeout)
      }

      if (type === 'code') {
        this.isCodeCopied = true
      }

      if (type === 'link') {
        this.isLinkCopied = true
      }

      this.copiedTimeout = setTimeout(() => {
        this.isCodeCopied = false
        this.isLinkCopied = false
        this.copiedTimeout = null
      }, 2000)
    },
  },

  destroyed() {
    this.socketLeaveRoom()
  },
}
</script>

<style lang="scss" scoped>
.fancy-text {
  @apply pointer-events-none fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transform text-center font-bungee text-7xl;
  text-shadow:
    0.01em 0.01em 0 #fff,
    0.02em 0.02em 0 #fff,
    0.03em 0.03em 0 #fff,
    0.04em 0.04em 0 #fff,
    0.05em 0.05em 0.1em #fff;
}
</style>



// WEBPACK FOOTER //
// src/views/OnlineGame.vue